import { useEffect, useMemo, useState } from "react";
import useAuth from "hooks/useAuth";
import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { getContactsThatHaveListingInFavoritesQuery } from "utils/Haim/firestoreUtils";
import { LISTING_PAGE_VIEWS_COLLECTION_NAME } from "../../../../@types/sharedSchema";
import type { FavoriteListingsOfContact, ListingPageViewsDocument } from "../../../../@types/sharedSchema";

export type ListingMinisiteState = {
  /** The viewer's minisite doc that already holds this listing, per contact. */
  favoriteByContact: Map<string, FavoriteListingsOfContact>;
  /** How many times each contact opened THIS listing inside their minisite. */
  viewsByContact: Map<string, number>;
  loading: boolean;
};

/**
 * Which matched contacts already have this listing in their minisite, and whether they opened
 * it — one live listener per listing (not per row), plus a one-shot read of the view counters.
 * Lifted out of the old card grid, where every card subscribed on its own.
 */
export function useListingMinisiteState(listingId: string | undefined, enabled = true): ListingMinisiteState {
  const { user } = useAuth();
  const email = user?.email as string | undefined;
  const [favorites, setFavorites] = useState<FavoriteListingsOfContact[]>([]);
  const [views, setViews] = useState<Record<string, ListingPageViewsDocument>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled || !listingId || !email) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubscribe = getContactsThatHaveListingInFavoritesQuery(email, listingId).onSnapshot(
      (snapshot) => {
        setFavorites(snapshot.docs.map((doc) => doc.data() as FavoriteListingsOfContact));
        setLoading(false);
      },
      (err) => {
        console.error("useListingMinisiteState: favorites listener failed", err);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [enabled, listingId, email]);

  const favoriteIdsKey = favorites
    .map((favorite) => favorite.id)
    .filter(Boolean)
    .sort()
    .join(",");

  useEffect(() => {
    if (!favoriteIdsKey) return;
    let cancelled = false;
    const ids = favoriteIdsKey.split(",");
    (async () => {
      const next: Record<string, ListingPageViewsDocument> = {};
      for (let i = 0; i < ids.length; i += 10) {
        const snapshots = await Promise.all(
          ids.slice(i, i + 10).map((id) =>
            db.collection(LISTING_PAGE_VIEWS_COLLECTION_NAME).doc(id).withConverter(GenericConverter<ListingPageViewsDocument>()).get()
          )
        );
        snapshots.forEach((snap) => {
          const data = snap.data();
          if (data?.contactId) next[data.contactId] = data;
        });
      }
      if (!cancelled) setViews(next);
    })().catch((err) => console.error("useListingMinisiteState: views read failed", err));
    return () => {
      cancelled = true;
    };
  }, [favoriteIdsKey]);

  return useMemo(() => {
    const favoriteByContact = new Map<string, FavoriteListingsOfContact>();
    favorites.forEach((favorite) => {
      if (favorite.contactId) favoriteByContact.set(favorite.contactId, favorite);
    });
    const viewsByContact = new Map<string, number>();
    Object.entries(views).forEach(([contactId, doc]) => {
      const count = listingId ? doc.listingDetailViewCounts?.[listingId] ?? 0 : 0;
      if (count > 0) viewsByContact.set(contactId, count);
    });
    return { favoriteByContact, viewsByContact, loading };
  }, [favorites, views, listingId, loading]);
}
